import axios from 'axios';

const base = window.__BCAPITAL_BASE__
  ? window.__BCAPITAL_BASE__.replace(/\/+$/, '')
  : '';
const API_BASE_URL = `${base}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      const base = window.__BCAPITAL_BASE__ || '/';
      window.location.href = `${base}login`;
    }
    return Promise.reject(error);
  }
);

// Dashboard API
export const dashboardApi = {
  getSummary: () => api.get('/dashboard/summary'),
  getPerformance: (period) => api.get(`/dashboard/performance?period=${period}`),
  getTopAssets: (limit = 5) => api.get(`/dashboard/top-assets?limit=${limit}`),
};

// Assets API
export const assetsApi = {
  getAll: (params = {}) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id, hard = false) => api.delete(`/assets/${id}?hard=${hard}`),
  blockToggle: (id, action) => api.post(`/assets/${id}/block`, { action }),
  bulkAction: (assetIds, action, data) => api.post('/assets/bulk-action', { assetIds, action, data }),
};

// Expenses API
export const expensesApi = {
  getAll: (params = {}) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getCategories: () => api.get('/expenses/categories'),
  getSummary: (params = {}) => api.get('/expenses/summary', { params }),
  reconcile: (data) => api.post('/expenses/reconcile', data),
};

// Bloomberg API
export const bloombergApi = {
  getFxRates: () => api.get('/bloomberg/fx'),
  updateFxRates: (rates, source) => api.post('/bloomberg/fx/update', { rates, source }),
  getQuote: (ticker) => api.get(`/bloomberg/quotes/${ticker}`),
  getQuotes: (tickers) => api.get('/bloomberg/quotes', { params: { tickers: tickers?.join(',') } }),
  importFile: (formData) => api.post('/bloomberg/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getImportHistory: () => api.get('/bloomberg/import/history'),
  getStatus: () => api.get('/bloomberg/status'),
  getCbrRates: () => api.post('/bloomberg/cbr'),
};

// Reports API
export const reportsApi = {
  getAll: (params = {}) => api.get('/reports', { params }),
  getTypes: () => api.get('/reports/types'),
  generatePortfolio: (data) => api.post('/reports/generate/portfolio', data),
  generateExpenses: (data) => api.post('/reports/generate/expenses', data),
  generateBeneficiary: (data) => api.post('/reports/generate/beneficiary', data),
  generateBeneficiaryDetailed: (data) => api.post('/reports/generate/beneficiary-detailed', data),
  download: (id) => api.get(`/reports/download/${id}`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/reports/${id}`),
};

// Holders API
export const holdersApi = {
  getAll: (params = {}) => api.get('/holders', { params }),
  getById: (id) => api.get(`/holders/${id}`),
  create: (data) => api.post('/holders', data),
  update: (id, data) => api.put(`/holders/${id}`, data),
  delete: (id) => api.delete(`/holders/${id}`),
  deactivate: (id) => api.post(`/holders/${id}/deactivate`),
  activate: (id) => api.post(`/holders/${id}/activate`),
};

// Operations API
export const operationsApi = {
  getAll: (params = {}) => api.get('/operations', { params }),
  getById: (id) => api.get(`/operations/${id}`),
  create: (data) => api.post('/operations', data),
  update: (id, data) => api.put(`/operations/${id}`, data),
  approve: (id) => api.post(`/operations/${id}/approve`),
  reject: (id, reason) => api.post(`/operations/${id}/reject`, { reason }),
  bulkApprove: (operationIds) => api.post('/operations/bulk-approve', { operationIds }),
  getPendingCount: () => api.get('/operations/pending/count'),
};

// Accounts API
export const accountsApi = {
  getAll: (params = {}) => api.get('/accounts', { params }),
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// Auth API
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (username, password, role) => api.post('/auth/register', { username, password, role }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.post('/settings', data),
  health: () => api.get('/health'),
};

// Beneficiaries API
export const beneficiariesApi = {
  getAll: (params = {}) => api.get('/beneficiaries', { params }),
  getById: (id) => api.get(`/beneficiaries/${id}`),
  getPortfolio: (id) => api.get(`/beneficiaries/${id}/portfolio`),
  create: (data) => api.post('/beneficiaries', data),
  update: (id, data) => api.put(`/beneficiaries/${id}`, data),
  delete: (id) => api.delete(`/beneficiaries/${id}`),
  deactivate: (id) => api.post(`/beneficiaries/${id}/deactivate`),
  activate: (id) => api.post(`/beneficiaries/${id}/activate`),
};

// Jurisdictions API
export const jurisdictionsApi = {
  getAll: (params = {}) => api.get('/jurisdictions', { params }),
  getById: (id) => api.get(`/jurisdictions/${id}`),
  getSummary: () => api.get('/jurisdictions/summary/all'),
  create: (data) => api.post('/jurisdictions', data),
  update: (id, data) => api.put(`/jurisdictions/${id}`, data),
  delete: (id) => api.delete(`/jurisdictions/${id}`),
  deactivate: (id) => api.post(`/jurisdictions/${id}/deactivate`),
  activate: (id) => api.post(`/jurisdictions/${id}/activate`),
};

// Bank Accounts API
export const bankAccountsApi = {
  getAll: (params = {}) => api.get('/bank-accounts', { params }),
  getById: (id) => api.get(`/bank-accounts/${id}`),
  getSummary: () => api.get('/bank-accounts/summary/all'),
  create: (data) => api.post('/bank-accounts', data),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/bank-accounts/${id}`),
  deactivate: (id) => api.post(`/bank-accounts/${id}/deactivate`),
  activate: (id) => api.post(`/bank-accounts/${id}/activate`),
};

// Investment Accounts API
export const investmentAccountsApi = {
  getAll: (params = {}) => api.get('/investment-accounts', { params }),
  getById: (id) => api.get(`/investment-accounts/${id}`),
  getSummary: () => api.get('/investment-accounts/summary/all'),
  getPortfolio: (id) => api.get(`/investment-accounts/${id}/portfolio`),
  create: (data) => api.post('/investment-accounts', data),
  update: (id, data) => api.put(`/investment-accounts/${id}`, data),
  delete: (id) => api.delete(`/investment-accounts/${id}`),
  deactivate: (id) => api.post(`/investment-accounts/${id}/deactivate`),
  activate: (id) => api.post(`/investment-accounts/${id}/activate`),
  recalculate: (id) => api.post(`/investment-accounts/${id}/recalculate`),
};

// Real Estate API
export const realEstateApi = {
  getAll: (params = {}) => api.get('/real-estate', { params }),
  getById: (id) => api.get(`/real-estate/${id}`),
  getSummary: () => api.get('/real-estate/summary/all'),
  create: (data) => api.post('/real-estate', data),
  update: (id, data) => api.put(`/real-estate/${id}`, data),
  delete: (id) => api.delete(`/real-estate/${id}`),
  deactivate: (id) => api.post(`/real-estate/${id}/deactivate`),
  activate: (id) => api.post(`/real-estate/${id}/activate`),
};

// Businesses API
export const businessesApi = {
  getAll: (params = {}) => api.get('/businesses', { params }),
  getById: (id) => api.get(`/businesses/${id}`),
  getSummary: () => api.get('/businesses/summary/all'),
  create: (data) => api.post('/businesses', data),
  update: (id, data) => api.put(`/businesses/${id}`, data),
  delete: (id) => api.delete(`/businesses/${id}`),
  deactivate: (id) => api.post(`/businesses/${id}/deactivate`),
  activate: (id) => api.post(`/businesses/${id}/activate`),
  updateStatus: (id, status) => api.post(`/businesses/${id}/status`, { status }),
  recordDividend: (id, amount, date) => api.post(`/businesses/${id}/dividend`, { amount, date }),
};

export default api;
